import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Shield, Activity, CheckCircle, XCircle, Clock, Hash, ChevronRight,
  Database, Globe, Zap, Lock, Eye, User, MessageSquare, Copy
} from 'lucide-react';
import { type Interaction } from './InteractionDrawer';

interface DomainMeta {
  id: string;
  label: string;
  passRate: string;
  totalInteractions: number;
  heroIds: string[];
}

interface DomainsSummary {
  totalInteractions: number;
  totalDomains: number;
  overallPassRate: string;
  ipLeaks: number;
  runs: number;
}

interface DomainsData {
  domains: DomainMeta[];
  summary: DomainsSummary;
}

const TIER_COLORS: Record<string, string> = {
  Analyst: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  Builder: 'bg-green-500/20 text-green-400 border-green-500/30',
  Operator: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  Strategist: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

function StatBlock({ icon: Icon, value, label }: { icon: React.ElementType; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="p-2 rounded-lg bg-zinc-800">
        <Icon className="w-4 h-4 text-cyan-400" />
      </div>
      <div>
        <div className="text-lg font-bold text-white leading-tight">{value}</div>
        <div className="text-xs text-zinc-500">{label}</div>
      </div>
    </div>
  );
}

function outcomeBadge(ix: Interaction) {
  if (ix.ipLeak) return { label: 'IP Leak', cls: 'bg-ag-critical/20 text-ag-critical border-ag-critical/30' };
  if (ix.falseClaim) return { label: 'False Claim', cls: 'bg-ag-critical/20 text-ag-critical border-ag-critical/30' };
  if (ix.confabulation) return { label: 'Confabulation Detected', cls: 'bg-ag-warning/20 text-ag-warning border-ag-warning/30' };
  return { label: 'Governance Verified', cls: 'bg-ag-active/20 text-ag-active border-ag-active/30' };
}

function CheckRow({ label, passed }: { label: string; passed: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-zinc-400">{label}</span>
      {passed ? (
        <span className="flex items-center gap-1 text-ag-active text-sm">
          <CheckCircle className="w-4 h-4" /> Pass
        </span>
      ) : (
        <span className="flex items-center gap-1 text-ag-critical text-sm">
          <XCircle className="w-4 h-4" /> Fail
        </span>
      )}
    </div>
  );
}

function InteractionCard({ ix, isSelected, onClick }: { ix: Interaction; isSelected: boolean; onClick: () => void }) {
  const outcome = outcomeBadge(ix);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg p-4 min-h-[120px] transition-all group
        ${isSelected
          ? 'border-cyan-500/50 bg-zinc-800/50 border'
          : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-600 hover:bg-zinc-900/80'
        }`}
    >
      {/* Domain tag */}
      <div className="mb-2">
        <span className="text-[10px] uppercase tracking-wider font-medium text-zinc-600 bg-zinc-800 px-1.5 py-0.5 rounded">
          {ix.domainLabel}
        </span>
      </div>

      <div className="flex items-start justify-between mb-2">
        <span className="text-sm font-medium text-zinc-200">{ix.persona.role}</span>
        <Badge className={`text-xs ${TIER_COLORS[ix.routingTier] || 'bg-zinc-700 text-zinc-300'}`}>
          {ix.routingTier}
        </Badge>
      </div>

      <p className="text-sm text-zinc-400 mb-2">{ix.question}</p>

      <p className="text-sm text-zinc-500 line-clamp-2 mb-3">{ix.response}</p>

      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Badge className={`text-[10px] ${outcome.cls}`}>{outcome.label}</Badge>
      </div>

      <div className="flex items-center gap-3 text-xs text-zinc-500">
        <span className="font-mono">{(ix.qualityScore * 100).toFixed(0)}%</span>
        <span className="flex items-center gap-1" title="IP Leak">
          <Lock className={`w-3 h-3 ${ix.ipLeak ? 'text-ag-critical' : 'text-ag-active'}`} />
        </span>
        <span className="flex items-center gap-1" title="Confabulation">
          <Eye className={`w-3 h-3 ${ix.confabulation ? 'text-ag-critical' : 'text-ag-active'}`} />
        </span>
        <span className="flex items-center gap-1" title="False Claim">
          <Shield className={`w-3 h-3 ${ix.falseClaim ? 'text-ag-critical' : 'text-ag-active'}`} />
        </span>
        <span className="ml-auto flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {(ix.latencyMs / 1000).toFixed(1)}s
        </span>
        <span className="font-mono text-zinc-600">R{ix.run}</span>
      </div>
    </button>
  );
}

function DetailPanel({ ix }: { ix: Interaction }) {
  const [copied, setCopied] = useState(false);
  const [responseCopied, setResponseCopied] = useState(false);

  const copyHash = useCallback(() => {
    navigator.clipboard.writeText(ix.responseHash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [ix]);

  const copyResponse = useCallback(() => {
    navigator.clipboard.writeText(ix.response).then(() => {
      setResponseCopied(true);
      setTimeout(() => setResponseCopied(false), 2000);
    });
  }, [ix]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-white">{ix.persona.name}</h3>
        <p className="text-sm text-zinc-400">{ix.persona.role}</p>
      </div>

      {/* Persona */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <User className="w-4 h-4" /> Persona
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Name</span>
            <span className="text-zinc-200">{ix.persona.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Role</span>
            <span className="text-zinc-200">{ix.persona.role}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Industry</span>
            <span className="text-zinc-200">{ix.persona.industry}</span>
          </div>
        </CardContent>
      </Card>

      {/* Question */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Question
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{ix.question}</p>
        </CardContent>
      </Card>

      {/* Response */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3 pt-4 px-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Response
          </CardTitle>
          <button
            onClick={copyResponse}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-xs bg-zinc-800 px-2 py-1 rounded transition-colors"
          >
            <Copy className="w-3 h-3" />
            {responseCopied ? 'Copied' : 'Copy Response'}
          </button>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <div className="max-h-[400px] overflow-y-auto">
            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{ix.response}</p>
          </div>
        </CardContent>
      </Card>

      {/* Routing */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Routing
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-500">Tier</span>
            <Badge className={`${TIER_COLORS[ix.routingTier] || 'bg-zinc-700 text-zinc-300'}`}>
              {ix.routingTier}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-500">Quality Score</span>
            <span className="text-sm font-mono text-zinc-200">{(ix.qualityScore * 100).toFixed(0)}%</span>
          </div>
        </CardContent>
      </Card>

      {/* Quality checks */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Quality Checks
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <CheckRow label="IP Leak Protection" passed={!ix.ipLeak} />
          <CheckRow label="Confabulation Check" passed={!ix.confabulation} />
          <CheckRow label="False Claim Check" passed={!ix.falseClaim} />
        </CardContent>
      </Card>

      {/* Metadata */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Metadata
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Latency</span>
            <span className="text-zinc-200 font-mono">{(ix.latencyMs / 1000).toFixed(1)}s</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Timestamp</span>
            <span className="text-zinc-200 font-mono text-xs">
              {new Date(ix.timestamp).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Run</span>
            <span className="text-zinc-200 font-mono">Run {ix.run}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-zinc-500 flex items-center gap-1">
              <Hash className="w-3 h-3" /> Response Hash
            </span>
            <button
              onClick={copyHash}
              className="flex items-center gap-1.5 text-zinc-300 hover:text-white font-mono text-xs bg-zinc-800 px-2 py-1 rounded transition-colors"
            >
              {ix.responseHash.slice(0, 19)}...
              <Copy className="w-3 h-3" />
              {copied && <span className="text-emerald-400 text-xs ml-1">Copied</span>}
            </button>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Verdict</span>
            <span className="text-zinc-200 font-mono text-xs">{ix.verdict}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Interaction ID</span>
            <span className="text-zinc-200 font-mono text-xs">{ix.id}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function EvidenceExplorer() {
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [domainsData, setDomainsData] = useState<DomainsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/evidence/interactions.json').then(r => r.json()),
      fetch('/evidence/domains.json').then(r => r.json())
    ]).then(([ixData, domData]) => {
      setInteractions(ixData);
      setDomainsData(domData);
      if (domData.domains.length > 0) {
        setSelectedDomain(domData.domains[0].id);
      }
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load evidence data:', err);
      setLoading(false);
    });
  }, []);

  const ixById = useMemo(() => {
    const map: Record<string, Interaction> = {};
    for (const ix of interactions) map[ix.id] = ix;
    return map;
  }, [interactions]);

  const domainInteractions = useMemo(() => {
    if (!selectedDomain) return [];
    return interactions.filter(ix => ix.domain === selectedDomain);
  }, [interactions, selectedDomain]);

  const activeDomainMeta = useMemo(() => {
    if (!domainsData || !selectedDomain) return null;
    return domainsData.domains.find(d => d.id === selectedDomain) || null;
  }, [domainsData, selectedDomain]);

  const heroInteractions = useMemo(() => {
    if (!activeDomainMeta) return [];
    return activeDomainMeta.heroIds
      .map(id => ixById[id])
      .filter((ix): ix is Interaction => ix !== undefined);
  }, [activeDomainMeta, ixById]);

  const visibleInteractions = useMemo(() => {
    if (expanded) return domainInteractions;
    return heroInteractions;
  }, [expanded, domainInteractions, heroInteractions]);

  const selectedInteraction = useMemo(() => {
    if (!selectedId) return null;
    return ixById[selectedId] || null;
  }, [selectedId, ixById]);

  const handleDomainClick = useCallback((id: string) => {
    setSelectedDomain(id);
    setExpanded(false);
    setSelectedId(null);
  }, []);

  const clawVariantCount = useMemo(() => {
    if (!domainsData) return 0;
    return domainsData.domains.filter(d =>
      d.label.toLowerCase().includes('claw') || d.label.toLowerCase().includes('openclaw')
    ).length;
  }, [domainsData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500 flex items-center gap-2">
          <Activity className="w-5 h-5 animate-pulse" /> Loading evidence data...
        </div>
      </div>
    );
  }

  if (!domainsData) {
    return (
      <div className="p-6 text-center text-zinc-500">
        Failed to load evidence data. Ensure the evidence files are generated.
      </div>
    );
  }

  const s = domainsData.summary;

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-white">Live Proof</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Real interactions from validated swarm runs. Every response independently verified.
        </p>
      </div>

      {/* Top stat bar */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <div className="flex flex-wrap divide-x divide-zinc-800">
            <StatBlock icon={Database} value={s.totalInteractions.toLocaleString()} label="Real Interactions" />
            <StatBlock icon={Globe} value={String(s.totalDomains)} label="Industries" />
            <StatBlock icon={CheckCircle} value={s.overallPassRate} label="Pass Rate" />
            <StatBlock icon={Lock} value={String(s.ipLeaks)} label="IP Leaks" />
            <StatBlock icon={Zap} value={String(s.runs)} label="Independent Runs" />
            {clawVariantCount > 0 && (
              <StatBlock icon={Activity} value={String(clawVariantCount)} label="Claw Variants" />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Domain pills */}
      <div className="overflow-x-auto pb-2 -mx-1">
        <div className="flex gap-2 px-1 min-w-max">
          {domainsData.domains.map(d => (
            <button
              key={d.id}
              onClick={() => handleDomainClick(d.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${selectedDomain === d.id
                  ? 'bg-[#C2714F]/20 text-cyan-400 border border-cyan-500/40'
                  : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600 hover:text-zinc-300'
                }`}
            >
              {d.label}
              <span className="ml-1.5 text-xs opacity-60">{d.passRate}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Domain header */}
      {activeDomainMeta && (
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">{activeDomainMeta.label}</h3>
            <p className="text-sm text-zinc-500">
              {activeDomainMeta.totalInteractions} interactions, {activeDomainMeta.passRate} pass rate
            </p>
          </div>
          {!expanded && domainInteractions.length > heroInteractions.length && (
            <button
              onClick={() => setExpanded(true)}
              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
            >
              See all {domainInteractions.length} <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {expanded && (
            <button
              onClick={() => setExpanded(false)}
              className="text-sm text-zinc-400 hover:text-zinc-300 transition-colors"
            >
              Show heroes only
            </button>
          )}
        </div>
      )}

      {/* Split-pane: card list + detail panel */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left column: card list */}
        <div className="w-full lg:w-[55%] overflow-y-auto space-y-3" style={{ maxHeight: '80vh' }}>
          {visibleInteractions.map(ix => (
            <InteractionCard
              key={ix.id}
              ix={ix}
              isSelected={selectedId === ix.id}
              onClick={() => setSelectedId(ix.id)}
            />
          ))}
          {visibleInteractions.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              No interactions found for this domain.
            </div>
          )}
        </div>

        {/* Right column: detail panel */}
        <div className="w-full lg:w-[45%] lg:sticky lg:top-0 lg:self-start" style={{ maxHeight: '80vh' }}>
          {selectedInteraction ? (
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 overflow-y-auto" style={{ maxHeight: '80vh' }}>
              <DetailPanel ix={selectedInteraction} />
            </div>
          ) : (
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 flex items-center justify-center" style={{ minHeight: '300px' }}>
              <p className="text-zinc-500 text-sm">Select an interaction to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
